import { PRIVACY_PAGE } from 'site-settings/site-navigation';
import {
  PrivacyTitle,
  PrivacyUpdated,
  PrivacySection,
  PrivacyH2,
  PrivacyH3,
  PrivacyP,
  PrivacyStrong,
  PrivacyUl,
  PrivacyOl,
  PrivacyLi,
  PrivacyMail,
  PrivacyLink,
  PrivacyUpper
} from './privacy-document.style';

type TermsOfUseContentProps = {
  /** When false, omit page title (used inside Create Account modal). */
  includeTitle?: boolean;
};

/** Official OFP Wallet Terms of Use (Open Forest AG, 23 Sep 2026). */
const TermsOfUseContent = ({ includeTitle = true }: TermsOfUseContentProps) => (
  <>
    {includeTitle && (
      <PrivacyTitle>TERMS OF USE OF OFP WALLET</PrivacyTitle>
    )}
    <PrivacyUpdated>Last updated: September 23rd, 2026</PrivacyUpdated>
    <PrivacySection>
      <PrivacyP>
        Open Forest AG, a company incorporated under the laws of Switzerland, registered under number <PrivacyStrong>CHE-396.866.692</PrivacyStrong>, with its registered office at c/o Mercandor AG, Unter Altstadt 28, 6300 <PrivacyStrong>Zug, Switzerland</PrivacyStrong> (<PrivacyStrong>“Open Forest AG,” “OF AG,” “we,” “us,” or “our”</PrivacyStrong>), develops, operates and manages technological infrastructure forming part of the Open Forest Protocol ecosystem, including the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        The OFP Wallet is a cryptographic wallet interface that enables users to interact with the Open Forest Protocol and supported blockchain infrastructure in order to receive, hold, view, transfer and otherwise manage supported digital assets, including <PrivacyStrong>OPN Tokens (“OPN”)</PrivacyStrong> and <PrivacyStrong>Open Carbon Credits (“OCCs”)</PrivacyStrong>.
      </PrivacyP>
      <PrivacyP>
        Certain components of the OFP Wallet may rely on third-party blockchain, infrastructure, authentication, hosting or other technology providers.
      </PrivacyP>
      <PrivacyP>
        These Terms of Use of OFP Wallet (the <PrivacyStrong>“Terms” or “Agreement”</PrivacyStrong>) govern your access to and use of the OFP Wallet and constitute an agreement between Open Forest AG and you, or the legal entity you represent (<PrivacyStrong>“you” or “User”</PrivacyStrong>).
      </PrivacyP>
      <PrivacyP>
        By accessing or using the OFP Wallet, or accepting these Terms where that option is made available, you:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>confirm that you have read and agree to these Terms and any policies incorporated into them; and</PrivacyLi>
        <PrivacyLi>
          acknowledge the collection and processing of information as described
          in the{' '}
          <PrivacyLink
            to={PRIVACY_PAGE}
            target="_blank"
            rel="noopener noreferrer"
          >
            applicable Privacy Policy
          </PrivacyLink>
          .
        </PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        If you do not agree to these Terms, you may not use the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        If you use the OFP Wallet on behalf of a company or other legal entity, you represent that you have authority to bind that entity to these Terms.
      </PrivacyP>
      <PrivacyP>
        You further represent that your use of the OFP Wallet is permitted under applicable law and that neither you nor, where applicable, the entity you represent is subject to sanctions or restrictions that prohibit such use.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>1. OFP WALLET</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>1.1 Generally</PrivacyH3>
      <PrivacyP>
        You may access and use the OFP Wallet in accordance with these Terms and applicable law.
      </PrivacyP>
      <PrivacyP>
        The OFP Wallet provides an interface through which Users may interact with supported digital assets and blockchain infrastructure.
      </PrivacyP>
      <PrivacyP>
        Depending on functionality available from time to time, this may include the ability to:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>view OPN and OCC balances;</PrivacyLi>
        <PrivacyLi>receive OPN and OCCs;</PrivacyLi>
        <PrivacyLi>transfer supported digital assets;</PrivacyLi>
        <PrivacyLi>view transaction history;</PrivacyLi>
        <PrivacyLi>interact with applicable smart contracts;</PrivacyLi>
        <PrivacyLi>manage OCCs associated with Open Forest Protocol projects;</PrivacyLi>
        <PrivacyLi>retire OCCs where such functionality is available; and</PrivacyLi>
        <PrivacyLi>access other functionality made available within the Open Forest Protocol ecosystem.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        The availability of functionality does not constitute a representation that a particular use of a digital asset is lawful or appropriate in every jurisdiction.
      </PrivacyP>
      <PrivacyH3>1.2 Non-Custodial Nature of the OFP Wallet</PrivacyH3>
      <PrivacyP>
        Unless expressly stated otherwise for a specific service, the OFP Wallet is <PrivacyStrong>non-custodial</PrivacyStrong>.
      </PrivacyP>
      <PrivacyP>
        This means that Open Forest AG does not hold or control your private keys, seed phrase or equivalent cryptographic credentials and does not have independent authority to initiate transactions using your digital assets.
      </PrivacyP>
      <PrivacyP>
        You retain responsibility for controlling and safeguarding the credentials associated with your Wallet.
      </PrivacyP>
      <PrivacyH3>1.3 Third-Party Content and Services</PrivacyH3>
      <PrivacyP>
        The OFP Wallet may display, integrate with or rely upon third-party content, infrastructure or services.
      </PrivacyP>
      <PrivacyP>
        These may include blockchain networks, wallet infrastructure, authentication providers, node providers, blockchain explorers, marketplaces or other third-party services.
      </PrivacyP>
      <PrivacyP>
        Your use of such third-party services may be subject to separate terms, policies and fees.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG does not control independent third-party services and is not responsible for their operation, availability, security or conduct.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>2. CHANGES</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>2.1 Changes to the OFP Wallet</PrivacyH3>
      <PrivacyP>
        We may change, update, add, restrict or discontinue functionality of the OFP Wallet from time to time.
      </PrivacyP>
      <PrivacyP>
        Such changes may be necessary for technical, operational, regulatory, security or Open Forest Protocol-related reasons.
      </PrivacyP>
      <PrivacyP>
        Where reasonably practicable, we will seek to communicate material changes or discontinuation through the OFP Wallet, Open Forest Protocol website, email or other appropriate communication channels.
      </PrivacyP>
      <PrivacyH3>2.2 Changes to these Terms</PrivacyH3>
      <PrivacyP>
        We may update these Terms from time to time.
      </PrivacyP>
      <PrivacyP>
        Where changes are material, we will use reasonable efforts to notify Users through the OFP Wallet, Open Forest Protocol website, email or another appropriate communication channel.
      </PrivacyP>
      <PrivacyP>
        Continued use of the OFP Wallet after updated Terms become effective constitutes acceptance of those Terms.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>3. YOUR RESPONSIBILITIES</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>3.1 Your OFP Wallet</PrivacyH3>
      <PrivacyP>
        You are responsible for activity initiated through your OFP Wallet and for ensuring that your use of the Wallet complies with applicable law.
      </PrivacyP>
      <PrivacyP>
        Except where caused directly by a breach by Open Forest AG of these Terms, Open Forest AG and its service providers are not responsible for unauthorized access resulting from circumstances outside their reasonable control, including fraud, phishing, compromised credentials, malware or other third-party activity.
      </PrivacyP>
      <PrivacyH3>3.2 Security and Backup</PrivacyH3>
      <PrivacyP>
        You are solely responsible for securing and backing up your OFP Wallet and associated credentials.
      </PrivacyP>
      <PrivacyP>
        This includes responsibility for securely maintaining any:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>passwords;</PrivacyLi>
        <PrivacyLi>private keys;</PrivacyLi>
        <PrivacyLi>backup or recovery keys;</PrivacyLi>
        <PrivacyLi>seed or recovery phrases;</PrivacyLi>
        <PrivacyLi>authentication credentials; or</PrivacyLi>
        <PrivacyLi>other information capable of providing access to your Wallet.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        You must not disclose private keys or seed phrases to Open Forest AG or to any person claiming to represent Open Forest AG.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG will not ask you to provide your full private key or seed phrase.
      </PrivacyP>
      <PrivacyP>
        If your private key, seed phrase or equivalent credentials are lost, Open Forest AG may be unable to recover access to your Wallet or digital assets.
      </PrivacyP>
      <PrivacyH3>3.3 Transaction Verification</PrivacyH3>
      <PrivacyP>
        Before authorizing a blockchain transaction, you are responsible for verifying all relevant information, including:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>recipient address;</PrivacyLi>
        <PrivacyLi>asset type;</PrivacyLi>
        <PrivacyLi>amount;</PrivacyLi>
        <PrivacyLi>blockchain network;</PrivacyLi>
        <PrivacyLi>transaction type; and</PrivacyLi>
        <PrivacyLi>any applicable network fees.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Blockchain transactions may be irreversible once submitted and confirmed.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG generally cannot cancel, reverse or recover a transaction sent to an incorrect address or otherwise incorrectly authorized by you.
      </PrivacyP>
      <PrivacyP>
        4. OPN AND OCCs
      </PrivacyP>
      <PrivacyH3>4.1 OPN</PrivacyH3>
      <PrivacyP>
        The OFP Wallet may allow Users to receive, hold, transfer or otherwise interact with OPN.
      </PrivacyP>
      <PrivacyP>
        The functionality and utility associated with OPN may evolve over time in accordance with the technical, governance, operational and legal development of the Open Forest Protocol.
      </PrivacyP>
      <PrivacyP>
        Nothing in these Terms constitutes a guarantee that OPN:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>will have or retain any particular value;</PrivacyLi>
        <PrivacyLi>will be tradable;</PrivacyLi>
        <PrivacyLi>will be listed on any exchange or marketplace;</PrivacyLi>
        <PrivacyLi>will have any particular level of liquidity;</PrivacyLi>
        <PrivacyLi>will provide financial returns;</PrivacyLi>
        <PrivacyLi>will maintain any particular functionality; or</PrivacyLi>
        <PrivacyLi>represents an ownership interest in Open Forest AG, Open Planet Foundation or any other entity.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Any rights or functionality associated with OPN are determined by the applicable technical architecture, smart contracts and relevant Open Forest Protocol documentation.
      </PrivacyP>
      <PrivacyH3>4.2 Open Carbon Credits</PrivacyH3>
      <PrivacyP>
        The OFP Wallet may allow Users to receive, hold, transfer, view, manage or retire OCCs.
      </PrivacyP>
      <PrivacyP>
        OCCs are issued in accordance with the applicable Open Forest Protocol standard, methodologies, validation requirements and technical rules.
      </PrivacyP>
      <PrivacyP>
        Registration of a project, submission or validation of a Stage Report, or use of the OFP Wallet does not in itself guarantee issuance of OCCs.
      </PrivacyP>
      <PrivacyP>
        The amount of OCCs issued may depend on validated Project Data, applicable carbon methodologies, uncertainty calculations, buffer or risk mechanisms and other applicable Open Forest Protocol requirements.
      </PrivacyP>
      <PrivacyH3>4.3 No Guarantee of Recognition or Value</PrivacyH3>
      <PrivacyP>
        Open Forest AG does not guarantee:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>the monetary value of an OPN or OCC;</PrivacyLi>
        <PrivacyLi>the existence or liquidity of a market for OPN or OCCs;</PrivacyLi>
        <PrivacyLi>acceptance of OCCs by any particular buyer;</PrivacyLi>
        <PrivacyLi>recognition of OCCs under any external carbon standard, regulatory framework or voluntary initiative; or</PrivacyLi>
        <PrivacyLi>the regulatory or tax treatment of OPN or OCCs in a particular jurisdiction.</PrivacyLi>
      </PrivacyUl>
      <PrivacyH3>4.4 Retirement of OCCs</PrivacyH3>
      <PrivacyP>
        Where the OFP Wallet permits retirement of OCCs, retirement may be irreversible.
      </PrivacyP>
      <PrivacyP>
        You are responsible for verifying the details and intended purpose of a retirement before authorizing it.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>5. FEES AND PAYMENT</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>5.1 Wallet Fees</PrivacyH3>
      <PrivacyP>
        Unless otherwise communicated, Open Forest AG does not charge Users a fee solely for accessing the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG may introduce charges for specific functionality or services in the future.
      </PrivacyP>
      <PrivacyP>
        Where reasonably practicable, material new fees charged directly by Open Forest AG will be communicated before they become effective.
      </PrivacyP>
      <PrivacyH3>5.2 Network Fees</PrivacyH3>
      <PrivacyP>
        Blockchain transactions may require payment of transaction, network or gas fees.
      </PrivacyP>
      <PrivacyP>
        Such fees may be determined by the relevant blockchain network and may vary depending on network conditions.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG does not control and cannot guarantee the amount of third-party network fees.
      </PrivacyP>
      <PrivacyH3>5.3 Taxes</PrivacyH3>
      <PrivacyP>
        You are responsible for identifying and paying taxes, duties, reporting obligations and governmental charges applicable to your acquisition, ownership, receipt, transfer, retirement or other use of digital assets.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG may withhold or report information where required by applicable law.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>6. SUSPENSION</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>6.1 Temporary Suspension</PrivacyH3>
      <PrivacyP>
        We may restrict or suspend access to some or all OFP Wallet interface functionality where we reasonably determine that:
      </PrivacyP>
      <PrivacyOl>
        <PrivacyLi>your use presents a security risk to the OFP Wallet, Open Forest Protocol, another User or a third party;</PrivacyLi>
        <PrivacyLi>your use could materially affect the operation or security of the OFP Wallet or Open Forest Protocol;</PrivacyLi>
        <PrivacyLi>your use may be unlawful;</PrivacyLi>
        <PrivacyLi>you have materially breached these Terms;</PrivacyLi>
        <PrivacyLi>the Wallet or associated account appears to have been compromised;</PrivacyLi>
        <PrivacyLi>suspension is necessary to investigate suspected fraud or misuse;</PrivacyLi>
        <PrivacyLi>suspension is required by law, court order, sanctions requirements or a competent authority; or</PrivacyLi>
        <PrivacyLi>suspension is reasonably necessary to protect the Open Forest Protocol ecosystem.</PrivacyLi>
      </PrivacyOl>
      <PrivacyH3>6.2 Effect of Suspension</PrivacyH3>
      <PrivacyP>
        Suspension of access to the OFP Wallet interface does not, by itself, transfer ownership of your digital assets to Open Forest AG or entitle Open Forest AG to confiscate those assets.
      </PrivacyP>
      <PrivacyP>
        Where the OFP Wallet is <PrivacyStrong>non-custodial</PrivacyStrong>, supported digital assets remain controlled through the relevant blockchain credentials.
      </PrivacyP>
      <PrivacyP>
        Depending on the relevant blockchain architecture, you may be able to access those assets through other compatible wallet software.
      </PrivacyP>
      <PrivacyP>
        However, particular functionality provided only through the Open Forest Protocol may not remain available during suspension.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>7. TERM AND TERMINATION</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>7.1 Term</PrivacyH3>
      <PrivacyP>
        These Terms apply from the date on which you first accept or use the OFP Wallet and continue until terminated.
      </PrivacyP>
      <PrivacyH3>7.2 Termination by the User</PrivacyH3>
      <PrivacyP>
        You may terminate these Terms by ceasing to use the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        Termination does not reverse or delete blockchain transactions previously initiated by you.
      </PrivacyP>
      <PrivacyH3>7.3 Termination by Open Forest AG</PrivacyH3>
      <PrivacyP>
        We may terminate or discontinue your access to the OFP Wallet where:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>you materially breach these Terms;</PrivacyLi>
        <PrivacyLi>we have grounds to suspend access under Section 6;</PrivacyLi>
        <PrivacyLi>continued provision of the Wallet would expose Open Forest AG to material legal, regulatory or security risk;</PrivacyLi>
        <PrivacyLi>a third-party technology necessary for the Wallet is no longer available;</PrivacyLi>
        <PrivacyLi>the OFP Wallet is discontinued; or</PrivacyLi>
        <PrivacyLi>applicable law requires termination.</PrivacyLi>
      </PrivacyUl>
      <PrivacyH3>7.4 Effect of Termination</PrivacyH3>
      <PrivacyP>
        Upon termination:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>your right to use the OFP Wallet interface under these Terms ends;</PrivacyLi>
        <PrivacyLi>outstanding obligations incurred before termination remain enforceable;</PrivacyLi>
        <PrivacyLi>termination does not give Open Forest AG ownership of your blockchain-based assets; and</PrivacyLi>
        <PrivacyLi>provisions intended by their nature to survive termination remain effective.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Where reasonably practicable, if Open Forest AG permanently discontinues the OFP Wallet, it will provide information reasonably necessary for Users to understand how supported assets may continue to be accessed.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>8. PROPRIETARY RIGHTS</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>8.1 OFP Wallet Licence</PrivacyH3>
      <PrivacyP>
        Open Forest AG owns, or holds the relevant licences to, the intellectual property rights in the OFP Wallet and related software, interfaces, documentation and technology, subject to any applicable open-source licences or third-party rights.
      </PrivacyP>
      <PrivacyP>
        Subject to these Terms, Open Forest AG grants you a limited, revocable, non-exclusive, non-sublicensable and non-transferable licence to access and use the OFP Wallet for its intended purposes.
      </PrivacyP>
      <PrivacyP>
        No ownership rights in the OFP Wallet or related technology are transferred to you.
      </PrivacyP>
      <PrivacyH3>8.2 Restrictions</PrivacyH3>
      <PrivacyP>
        Except as expressly permitted by applicable law or an applicable open-source licence, you shall not:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>modify or create unauthorized derivative works of the OFP Wallet;</PrivacyLi>
        <PrivacyLi>reverse engineer, disassemble or decompile proprietary components;</PrivacyLi>
        <PrivacyLi>interfere with the operation or security of the OFP Wallet;</PrivacyLi>
        <PrivacyLi>use automated scraping or extraction tools to improperly obtain data;</PrivacyLi>
        <PrivacyLi>circumvent technical restrictions;</PrivacyLi>
        <PrivacyLi>resell access to the OFP Wallet; or</PrivacyLi>
        <PrivacyLi>misuse Open Forest Protocol trademarks or branding.</PrivacyLi>
      </PrivacyUl>
      <PrivacyH3>8.3 Open-Source Components</PrivacyH3>
      <PrivacyP>
        Certain parts of the Open Forest Protocol or OFP Wallet may be made available under open-source licences.
      </PrivacyP>
      <PrivacyP>
        Where an applicable open-source licence conflicts with these Terms regarding the relevant software component, the open-source licence shall govern that component.
      </PrivacyP>
      <PrivacyH3>8.4 Suggestions</PrivacyH3>
      <PrivacyP>
        If you voluntarily provide suggestions or feedback concerning the OFP Wallet, Open Forest AG may use that feedback without restriction or compensation, unless otherwise agreed in writing.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>9. ACCEPTABLE USE</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        You agree not to use the OFP Wallet:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>for unlawful, fraudulent or deceptive purposes;</PrivacyLi>
        <PrivacyLi>to violate the legal rights of another person;</PrivacyLi>
        <PrivacyLi>for money laundering, terrorist financing or other prohibited financial activity;</PrivacyLi>
        <PrivacyLi>in violation of applicable sanctions;</PrivacyLi>
        <PrivacyLi>to knowingly interact with unlawfully obtained assets;</PrivacyLi>
        <PrivacyLi>to distribute malware or malicious software;</PrivacyLi>
        <PrivacyLi>to exploit or attempt to exploit vulnerabilities;</PrivacyLi>
        <PrivacyLi>to interfere with the OFP Wallet, Open Forest Protocol or associated infrastructure;</PrivacyLi>
        <PrivacyLi>to impersonate another person or entity;</PrivacyLi>
        <PrivacyLi>to gain unauthorized access to another Wallet or account; or</PrivacyLi>
        <PrivacyLi>in any way intended to compromise the integrity or security of the Open Forest Protocol.</PrivacyLi>
      </PrivacyUl>
    </PrivacySection>
    <PrivacyH2>10. BLOCKCHAIN AND DIGITAL ASSET RISKS</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>10.1 Blockchain Technology</PrivacyH3>
      <PrivacyP>
        The OFP Wallet relies on blockchain and cryptographic technologies.
      </PrivacyP>
      <PrivacyP>
        By using the OFP Wallet, you acknowledge that these technologies involve risks, including:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>loss or compromise of private keys;</PrivacyLi>
        <PrivacyLi>irreversible transactions;</PrivacyLi>
        <PrivacyLi>software vulnerabilities;</PrivacyLi>
        <PrivacyLi>smart-contract vulnerabilities;</PrivacyLi>
        <PrivacyLi>blockchain network failures;</PrivacyLi>
        <PrivacyLi>forks or network changes;</PrivacyLi>
        <PrivacyLi>cybersecurity incidents;</PrivacyLi>
        <PrivacyLi>regulatory changes;</PrivacyLi>
        <PrivacyLi>changes in transaction costs or speed;</PrivacyLi>
        <PrivacyLi>market volatility; and</PrivacyLi>
        <PrivacyLi>incompatibility between software, protocols or networks.</PrivacyLi>
      </PrivacyUl>
      <PrivacyH3>10.2 Blockchain Infrastructure</PrivacyH3>
      <PrivacyP>
        Open Forest AG does not control the operation of independent public blockchain networks.
      </PrivacyP>
      <PrivacyP>
        Transaction confirmation times, availability, fees and other characteristics may therefore be outside Open Forest AG's control.
      </PrivacyP>
      <PrivacyH3>10.3 Smart Contracts</PrivacyH3>
      <PrivacyP>
        The OFP Wallet may interact with smart contracts.
      </PrivacyP>
      <PrivacyP>
        Despite reasonable efforts in development, testing and review, smart contracts may contain defects, vulnerabilities or unintended functionality.
      </PrivacyP>
      <PrivacyP>
        Smart-contract transactions may be irreversible.
      </PrivacyP>
      <PrivacyH3>10.4 User Responsibility</PrivacyH3>
      <PrivacyP>
        You are responsible for determining whether you have sufficient understanding of blockchain technologies and digital assets to use the OFP Wallet safely.
      </PrivacyP>
      <PrivacyP>
        You are solely responsible for approvals and permissions provided through your cryptographic signature.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>11. NO INVESTMENT, FINANCIAL OR TAX ADVICE</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        The OFP Wallet is technological infrastructure for interacting with supported digital assets.
      </PrivacyP>
      <PrivacyP>
        Nothing provided through the OFP Wallet constitutes:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>investment advice;</PrivacyLi>
        <PrivacyLi>financial advice;</PrivacyLi>
        <PrivacyLi>legal advice;</PrivacyLi>
        <PrivacyLi>tax advice; or</PrivacyLi>
        <PrivacyLi>a recommendation to acquire, hold, transfer or dispose of any digital asset.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Information concerning OPN, OCCs or other digital assets should not be interpreted as a promise of financial performance or appreciation in value.
      </PrivacyP>
      <PrivacyP>
        You should obtain independent professional advice where appropriate.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>12. INDEMNIFICATION</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        To the extent permitted by applicable law, you shall indemnify and hold harmless Open Forest AG and its affiliates, service providers, directors, officers, employees and representatives from reasonable losses, liabilities and costs arising from third-party claims resulting from:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>your material breach of these Terms;</PrivacyLi>
        <PrivacyLi>your unlawful use of the OFP Wallet;</PrivacyLi>
        <PrivacyLi>your infringement of third-party rights; or</PrivacyLi>
        <PrivacyLi>activity undertaken through the Wallet by persons for whom you are legally responsible.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        This Section does not apply to the extent that a claim results from Open Forest AG's own unlawful conduct.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>13. DISCLAIMERS</PrivacyH2>
    <PrivacySection>
      <PrivacyUpper>
        THE OFP WALLET IS PROVIDED ON AN <PrivacyStrong>“AS IS”</PrivacyStrong>{' '}
        AND <PrivacyStrong>“AS AVAILABLE”</PrivacyStrong> BASIS.
      </PrivacyUpper>
      <PrivacyUpper>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, OPEN FOREST AG AND ITS SERVICE
        PROVIDERS MAKE NO WARRANTY THAT:
      </PrivacyUpper>
      <PrivacyUl>
        <PrivacyLi>THE OFP WALLET WILL BE UNINTERRUPTED OR ERROR-FREE;</PrivacyLi>
        <PrivacyLi>THE OFP WALLET WILL BE FREE FROM ALL SECURITY VULNERABILITIES;</PrivacyLi>
        <PrivacyLi>THIRD-PARTY BLOCKCHAIN INFRASTRUCTURE WILL REMAIN AVAILABLE;</PrivacyLi>
        <PrivacyLi>DIGITAL ASSETS WILL RETAIN ANY PARTICULAR VALUE OR FUNCTIONALITY;</PrivacyLi>
        <PrivacyLi>TRANSACTIONS WILL BE CONFIRMED WITHIN ANY PARTICULAR PERIOD;</PrivacyLi>
        <PrivacyLi>OPN OR OCCs WILL BE LIQUID OR TRANSFERABLE; OR</PrivacyLi>
        <PrivacyLi>OCCs WILL BE RECOGNIZED OR ACCEPTED BY ANY PARTICULAR THIRD PARTY OR REGULATORY REGIME.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Nothing in this Section excludes warranties or rights that cannot lawfully be excluded.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>14. LIMITATION OF LIABILITY</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        To the maximum extent permitted by applicable law, Open Forest AG and its affiliates and service providers shall not be liable for indirect, incidental, consequential, special or punitive losses arising out of the use of or inability to use the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        Without limiting the foregoing, and except where caused directly by conduct for which liability cannot lawfully be excluded, Open Forest AG shall not be responsible for losses arising from:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>loss or compromise of private keys, passwords or seed phrases;</PrivacyLi>
        <PrivacyLi>unauthorized Wallet access outside Open Forest AG's reasonable control;</PrivacyLi>
        <PrivacyLi>transactions authorized by the User;</PrivacyLi>
        <PrivacyLi>transactions sent to an incorrect blockchain address;</PrivacyLi>
        <PrivacyLi>irreversible blockchain transactions;</PrivacyLi>
        <PrivacyLi>changes in digital asset market value;</PrivacyLi>
        <PrivacyLi>lack of liquidity;</PrivacyLi>
        <PrivacyLi>network congestion;</PrivacyLi>
        <PrivacyLi>third-party blockchain failures;</PrivacyLi>
        <PrivacyLi>third-party services;</PrivacyLi>
        <PrivacyLi>user configuration errors;</PrivacyLi>
        <PrivacyLi>malware or phishing;</PrivacyLi>
        <PrivacyLi>blockchain forks;</PrivacyLi>
        <PrivacyLi>smart-contract vulnerabilities; or</PrivacyLi>
        <PrivacyLi>changes in law or regulatory treatment.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Nothing in these Terms excludes or limits liability where exclusion or limitation is prohibited by applicable law.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>15. PRIVACY AND BLOCKCHAIN DATA</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Personal Data processed in connection with the OFP Wallet is handled in accordance with Open Forest AG's <PrivacyLink to={PRIVACY_PAGE} target="_blank" rel="noopener noreferrer">applicable Privacy Policy</PrivacyLink> and applicable data protection law.
      </PrivacyP>
      <PrivacyP>
        Users acknowledge that:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>public blockchain addresses may be publicly visible;</PrivacyLi>
        <PrivacyLi>blockchain transactions may be permanently recorded;</PrivacyLi>
        <PrivacyLi>transaction information may be accessible to third parties; and</PrivacyLi>
        <PrivacyLi>information recorded directly on a public blockchain may not be capable of being modified or deleted by Open Forest AG.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Authorized third-party service providers may process relevant information on behalf of Open Forest AG where required to operate, secure or maintain the OFP Wallet.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>16. SANCTIONS AND REGULATORY COMPLIANCE</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        You must comply with applicable sanctions, anti-money laundering, counter-terrorist financing and other applicable laws when using the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG may request information reasonably necessary to:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>establish or verify identity;</PrivacyLi>
        <PrivacyLi>assess suspicious activity;</PrivacyLi>
        <PrivacyLi>comply with applicable law;</PrivacyLi>
        <PrivacyLi>respond to competent authorities; or</PrivacyLi>
        <PrivacyLi>protect the Open Forest Protocol.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Where permitted or required by law, Open Forest AG may restrict access to its own interfaces and services.
      </PrivacyP>
      <PrivacyP>
        Because public blockchains operate independently of Open Forest AG, restricting access to the OFP Wallet interface may not prevent a User from accessing blockchain-based assets through other compatible software.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>17. THIRD-PARTY TECHNOLOGY</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        Open Forest AG may rely on third-party technology and infrastructure providers in connection with the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        Such providers may include blockchain networks, hosting providers, authentication systems, node providers, wallet infrastructure, blockchain explorers or other service providers.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG does not control independent third-party services and does not guarantee their continued operation, availability or compatibility.
      </PrivacyP>
      <PrivacyP>
        Where a third-party service is subject to separate terms, your use of that service may also be subject to those terms.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>18. MISCELLANEOUS</PrivacyH2>
    <PrivacySection>
      <PrivacyH3>18.1 Assignment</PrivacyH3>
      <PrivacyP>
        You may not assign these Terms or transfer your rights under them without our prior written consent.
      </PrivacyP>
      <PrivacyP>
        Open Forest AG may assign these Terms in connection with a merger, acquisition, corporate reorganization, transfer of relevant assets or transfer of operation of the OFP Wallet, subject to applicable law.
      </PrivacyP>
      <PrivacyH3>18.2 Entire Agreement</PrivacyH3>
      <PrivacyP>
        These Terms, the <PrivacyLink to={PRIVACY_PAGE} target="_blank" rel="noopener noreferrer">applicable Privacy Policy</PrivacyLink> and any policies expressly incorporated into them constitute the agreement between you and Open Forest AG concerning use of the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        Any separate agreement governing OPN, OCCs, project participation, validation or another Open Forest Protocol service remains applicable to the matters it specifically regulates.
      </PrivacyP>
      <PrivacyH3>18.3 Force Majeure</PrivacyH3>
      <PrivacyP>
        Neither Party shall be liable for delay or failure caused by circumstances beyond its reasonable control, including natural disasters, telecommunications failures, blockchain outages, cyberattacks, governmental action, war, civil unrest or similar events.
      </PrivacyP>
      <PrivacyH3>18.4 Independent Parties</PrivacyH3>
      <PrivacyP>
        Nothing in these Terms creates a partnership, agency, employment relationship or joint venture between you and Open Forest AG.
      </PrivacyP>
      <PrivacyH3>18.5 Notices</PrivacyH3>
      <PrivacyP>
        Open Forest AG may provide notices through:
      </PrivacyP>
      <PrivacyUl>
        <PrivacyLi>the OFP Wallet;</PrivacyLi>
        <PrivacyLi>the Open Forest Protocol website;</PrivacyLi>
        <PrivacyLi>an email address associated with your account; or</PrivacyLi>
        <PrivacyLi>another reasonable communication channel.</PrivacyLi>
      </PrivacyUl>
      <PrivacyP>
        Notices to Open Forest AG may be sent to:
      </PrivacyP>
      <PrivacyP>
        <PrivacyMail href="mailto:support@openforestprotocol.org">support@openforestprotocol.org</PrivacyMail>
      </PrivacyP>
      <PrivacyH3>18.6 No Third-Party Beneficiaries</PrivacyH3>
      <PrivacyP>
        Except where expressly stated, these Terms do not create rights for persons or entities that are not parties to them.
      </PrivacyP>
      <PrivacyH3>18.7 No Waiver</PrivacyH3>
      <PrivacyP>
        Failure to enforce a provision of these Terms does not constitute a waiver of that provision.
      </PrivacyP>
      <PrivacyH3>18.8 Severability</PrivacyH3>
      <PrivacyP>
        If a provision of these Terms is held to be invalid or unenforceable, the remaining provisions remain in effect.
      </PrivacyP>
      <PrivacyH3>18.9 Language</PrivacyH3>
      <PrivacyP>
        These Terms are drafted in English.
      </PrivacyP>
      <PrivacyP>
        Translations may be provided for convenience. In the event of inconsistency, the English version shall prevail.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>19. GOVERNING LAW AND JURISDICTION</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        These Terms and any dispute arising out of or in connection with them shall be governed by Swiss substantive law.
      </PrivacyP>
      <PrivacyP>
        Any claim arising out of or in connection with these Terms or use of the OFP Wallet shall be brought before the competent ordinary courts of <PrivacyStrong>Zug, Switzerland</PrivacyStrong>, subject to any mandatory jurisdiction required by applicable law.
      </PrivacyP>
    </PrivacySection>
    <PrivacyH2>20. DEFINITIONS</PrivacyH2>
    <PrivacySection>
      <PrivacyP>
        <PrivacyStrong>“Blockchain”</PrivacyStrong> means a decentralized or distributed ledger network supported by the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“Digital Asset”</PrivacyStrong> means OPN, an OCC or another blockchain-based asset supported by the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“OCC”</PrivacyStrong> or{' '}
        <PrivacyStrong>“Open Carbon Credit”</PrivacyStrong> means a digital
        environmental asset issued or recorded through the Open Forest Protocol
        in accordance with the applicable OFP Standard, methodology and
        technical rules.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“OFP Wallet”</PrivacyStrong> means the cryptographic
        wallet interface managed by Open Forest AG through which Users can
        interact with supported digital assets and the Open Forest Protocol.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“OPN”</PrivacyStrong> or{' '}
        <PrivacyStrong>“OPN Token”</PrivacyStrong> means the blockchain-based
        token associated with the Open Forest Protocol.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“Private Key”</PrivacyStrong> includes any private
        cryptographic key, seed phrase, recovery phrase, passkey or equivalent
        credential capable of authorizing access to or transactions involving
        digital assets.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“Smart Contract”</PrivacyStrong> means software deployed on a blockchain that executes or facilitates blockchain-based transactions or functions.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“Transaction”</PrivacyStrong> means any blockchain transaction initiated through or in connection with the OFP Wallet.
      </PrivacyP>
      <PrivacyP>
        <PrivacyStrong>“User”</PrivacyStrong> means a natural person or legal entity accessing or using the OFP Wallet.
      </PrivacyP>
    </PrivacySection>
  </>
);

export default TermsOfUseContent;

